import { useMemo } from 'react'

import { DARK_THEME, LIGHT_THEME, PartialTheme, Theme } from '@elastic/charts'
// @ts-ignore
import { mergePartial } from '@elastic/charts/dist/utils/common'
import { useComputedColorScheme, useMantineTheme } from '@tidbcloud/uikit'

import { chartColors, chartColorsOpaque, chartColorsWhell } from './chart.color'
import { alertColors } from './chart.color.alert'
import { chartDarkColors, chartLightColors } from './chart.color.theme'

export function useChartColors() {
  const { colors } = useMantineTheme()
  const colorScheme = useComputedColorScheme()
  const chartColorsOpacity08 = useMemo(
    () =>
      chartColorsWhell.reduce((acc, cur) => acc.concat(cur[4]), [] as string[]),
    []
  )
  const chartColorsOpacity06 = useMemo(
    () =>
      chartColorsWhell.reduce((acc, cur) => acc.concat(cur[3]), [] as string[]),
    []
  )
  const chartColorsOpacity04 = useMemo(
    () =>
      chartColorsWhell.reduce((acc, cur) => acc.concat(cur[2]), [] as string[]),
    []
  )
  const chartColorsOpacity02 = useMemo(
    () =>
      chartColorsWhell.reduce((acc, cur) => acc.concat(cur[1]), [] as string[]),
    []
  )
  const chartColorsOpacity01 = useMemo(
    () =>
      chartColorsWhell.reduce((acc, cur) => acc.concat(cur[0]), [] as string[]),
    []
  )
  const chartThemeColors = useMemo(
    () => (colorScheme === 'dark' ? chartDarkColors : chartLightColors),
    [colorScheme]
  )

  return useMemo(
    () => ({
      themeColors: colors,
      chartThemeColors,
      chartColors,
      chartColorsOpaque,
      alertColors,
      chartColorsWhell,
      chartColorsOpacity01,
      chartColorsOpacity02,
      chartColorsOpacity04,
      chartColorsOpacity06,
      chartColorsOpacity08
    }),
    [
      colors,
      chartThemeColors,
      chartColorsOpacity01,
      chartColorsOpacity02,
      chartColorsOpacity04,
      chartColorsOpacity06,
      chartColorsOpacity08
    ]
  )
}

export function useChartThemeConfig() {
  const colorScheme = useComputedColorScheme()
  const { themeColors, chartColorsOpacity08 } = useChartColors()
  const overrideTheme: PartialTheme = useMemo(
    () => ({
      colors: {
        vizColors: chartColorsOpacity08
      },
      axes: {
        axisLine: { stroke: themeColors.carbon[4] },
        tickLabel: {
          fill: themeColors.carbon[7],
          padding: { inner: 10 }
        },
        tickLine: {
          size: 0
        },
        axisTitle: {
          fill: themeColors.carbon[9]
        },
        gridLine: {
          horizontal: {
            visible: true,
            dash: [3, 3],
            stroke: themeColors.carbon[4]
          },
          vertical: {
            visible: true,
            dash: [3, 3],
            stroke: themeColors.carbon[4]
          }
        }
      }
    }),
    [chartColorsOpacity08, themeColors]
  )
  const themeConfig: Theme = useMemo(
    () =>
      mergePartial(
        colorScheme === 'dark' ? DARK_THEME : LIGHT_THEME,
        overrideTheme
      ),
    [colorScheme, overrideTheme]
  )

  return useMemo(
    () => ({
      themeConfig
    }),
    [themeConfig]
  )
}
